import React, { FC, ReactNode, useCallback, useEffect, useRef } from "react";
import { Transition } from "react-transition-group";
import styled from "styled-components";

// region ── Types ──

export interface ExpandCollapseTransitionProps {
    /** When true the content animates open; when false it animates closed. */
    in: boolean;
    /** Animation duration in milliseconds. Default: 250. */
    duration?: number;
    /** Keep the content mounted while collapsed. Default: false (unmount when closed). */
    keepMounted?: boolean;
    /** Passed through to the animated wrapper (e.g. id, role, aria-*). */
    id?: string;
    role?: string;
    "aria-labelledby"?: string;
    /** Content to reveal. */
    children: ReactNode;
}

// endregion

// region ── Styles ──

const DEFAULT_DURATION = 250;

// The repo's styled-components typings drop the base div props (children, ref,
// aria-*) once a generic is supplied, so re-attach them via a single typed cast.
const AnimatedPanel = styled.div<{ $duration: number }>`
    overflow: hidden;
    transition:
        max-height ${({ $duration }) => $duration}ms ease,
        opacity ${({ $duration }) => $duration}ms ease;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
` as unknown as React.FC<
    { $duration: number } & React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }
>;

// endregion

// region ── Component ──

/**
 * Animates its children open/closed by measuring their real height, so content
 * of any size expands and collapses smoothly. Avoids the common `max-height`
 * pitfalls (fixed max clipping tall content, and the inability to transition
 * to/from `max-height: none`).
 */
const ExpandCollapseTransition: FC<ExpandCollapseTransitionProps> = ({
    in: inProp,
    duration = DEFAULT_DURATION,
    keepMounted = false,
    id,
    role,
    "aria-labelledby": ariaLabelledBy,
    children,
}) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const isOpenRef = useRef(inProp);
    isOpenRef.current = inProp;

    // Clears the animation caps so the expanded panel can overflow again (dropdowns,
    // popovers, focus rings). Safe to call more than once and ignored while collapsing.
    const settleOpen = useCallback(() => {
        const node = panelRef.current;
        if (!node || !isOpenRef.current) return;
        node.style.removeProperty("max-height");
        node.style.removeProperty("opacity");
        node.style.overflow = "visible";
        // Expose the now-visible content to assistive tech and the tab order.
        node.removeAttribute("aria-hidden");
        node.removeAttribute("inert");
    }, []);

    const handleEnter = useCallback(() => {
        const node = panelRef.current;
        if (!node) return;
        node.style.overflow = "hidden";
        node.style.maxHeight = "0px";
        node.style.opacity = "0";
    }, []);

    const handleEntering = useCallback(() => {
        const node = panelRef.current;
        if (!node) return;
        node.style.maxHeight = `${node.scrollHeight}px`;
        node.style.opacity = "1";
    }, []);

    // Safety net: the transition lifecycle can miss the entered callback when the panel
    // is unmounted and remounted across toggles, which would leave overflow: hidden stuck.
    useEffect(() => {
        if (!inProp) return undefined;
        const timer = window.setTimeout(settleOpen, duration + 50);
        return () => window.clearTimeout(timer);
    }, [inProp, duration, settleOpen]);

    const handleTransitionEnd = useCallback(
        (event: React.TransitionEvent<HTMLDivElement>) => {
            if (event.target !== panelRef.current || event.propertyName !== "max-height") return;
            settleOpen();
        },
        [settleOpen],
    );

    const handleExit = useCallback(() => {
        const node = panelRef.current;
        if (!node) return;
        node.style.overflow = "hidden";
        node.style.maxHeight = `${node.scrollHeight}px`;
        node.style.opacity = "1";
        // Force a reflow so the browser registers the starting height before collapsing.
        void node.offsetHeight;
    }, []);

    const handleExiting = useCallback(() => {
        const node = panelRef.current;
        if (!node) return;
        node.style.maxHeight = "0px";
        node.style.opacity = "0";
        // Hide collapsed content from assistive tech and remove its focusable
        // children from the tab order (relevant when keepMounted keeps it in the DOM).
        node.setAttribute("aria-hidden", "true");
        node.setAttribute("inert", "");
    }, []);

    // Apply the collapsed baseline for the initial closed mount (relevant when
    // keepMounted is set); the lifecycle callbacks manage every state after that.
    // Kept stable so toggling `in` never re-runs it mid-animation.
    const didInitRef = useRef(false);
    const setPanelRef = useCallback((node: HTMLDivElement | null) => {
        panelRef.current = node;
        if (node && !didInitRef.current) {
            didInitRef.current = true;
            if (!isOpenRef.current) {
                node.style.overflow = "hidden";
                node.style.maxHeight = "0px";
                node.style.opacity = "0";
                node.setAttribute("aria-hidden", "true");
                node.setAttribute("inert", "");
            }
        }
    }, []);

    return (
        <Transition
            nodeRef={panelRef}
            in={inProp}
            timeout={duration}
            mountOnEnter={!keepMounted}
            unmountOnExit={!keepMounted}
            onEnter={handleEnter}
            onEntering={handleEntering}
            onEntered={settleOpen}
            onExit={handleExit}
            onExiting={handleExiting}
        >
            {() => (
                <AnimatedPanel
                    ref={setPanelRef}
                    $duration={duration}
                    id={id}
                    role={role}
                    aria-labelledby={ariaLabelledBy}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {children}
                </AnimatedPanel>
            )}

        </Transition>
    );
};

// endregion

export default ExpandCollapseTransition;
